import Image from 'next/image';
import Link from 'next/link';
import { ArrowRight, BookOpen, Calculator, LockKeyhole, Moon, Orbit, ShieldCheck } from 'lucide-react';

const tools = [
    { title: 'Life Path Number', body: 'Calculate your core numerology pattern and open the matching number guide.', href: '/life-path-number-calculator', icon: Calculator },
    { title: 'Moon Sign', body: 'See why emotional needs can differ from your public sun-sign personality.', href: '/moon-sign-calculator', icon: Moon },
    { title: 'Birth Chart', body: 'Start with your birth details and build toward a complete chart-aware reading.', href: '/free-birth-chart-reading', icon: Orbit },
];

const guides = [
    { label: 'Astrology guides and tools', href: '/astrology', body: 'Birth charts, moon signs, houses, timing, and relationship patterns.' },
    { label: 'Numerology guides and tools', href: '/numerology', body: 'Life path meanings, master numbers, personal cycles, and compatibility.' },
    { label: 'Editorial methodology', href: '/methodology', body: 'How YourAura combines traditional systems, deterministic calculations, and AI-assisted interpretation.' },
];

export function PopularToolsSection() {
    return (
        <section id="free-tools" className="bg-background px-5 py-20 md:py-24" aria-labelledby="free-tools-title">
            <div className="container mx-auto max-w-6xl">
                <div className="max-w-3xl">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">Popular free tools</p>
                    <h2 id="free-tools-title" className="mt-3 font-playfair text-3xl font-bold text-foreground md:text-5xl">Start with one clear answer</h2>
                    <p className="mt-4 text-lg leading-8 text-muted-foreground">No account is required for the first result. Choose the question that feels most useful today.</p>
                </div>
                <div className="mt-9 grid gap-5 md:grid-cols-3">
                    {tools.map((tool) => {
                        const ToolIcon = tool.icon;
                        return (
                            <Link key={tool.href} href={tool.href} className="card-surface card-interactive group flex h-full flex-col p-6">
                                <span className="card-icon"><ToolIcon size={21} aria-hidden="true" /></span>
                                <h3 className="mt-5 font-playfair text-2xl font-semibold text-foreground">{tool.title}</h3>
                                <p className="mt-3 min-h-20 text-sm leading-7 text-muted-foreground">{tool.body}</p>
                                <span className="card-footer text-sm font-bold text-accent">Get free result <ArrowRight size={16} aria-hidden="true" /></span>
                            </Link>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

export function MethodologySection() {
    return (
        <section id="methodology" className="border-y border-[var(--card-border)] bg-[var(--card-surface)] px-5 py-20 md:py-24" aria-labelledby="methodology-title">
            <div className="container mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-[1fr_0.9fr]">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">How results are built</p>
                    <h2 id="methodology-title" className="mt-3 font-playfair text-3xl font-bold text-foreground md:text-5xl">Transparent calculation before interpretation</h2>
                    <p className="mt-5 text-lg leading-8 text-muted-foreground">Public calculators use explicit birth-date and profile inputs. AI-assisted language is reserved for deeper, signed-in readings where your saved context can make the interpretation more useful.</p>
                    <div className="mt-7 grid gap-4 sm:grid-cols-2">
                        <div className="flex gap-3"><ShieldCheck className="mt-1 h-5 w-5 shrink-0 text-accent" /><p className="text-sm leading-6 text-muted-foreground"><strong className="block text-foreground">Reflective, not deterministic</strong>No medical, legal, financial, or fixed-fate claims.</p></div>
                        <div className="flex gap-3"><LockKeyhole className="mt-1 h-5 w-5 shrink-0 text-accent" /><p className="text-sm leading-6 text-muted-foreground"><strong className="block text-foreground">Private by default</strong>Your profile stays private unless you deliberately share a result.</p></div>
                    </div>
                    <div className="mt-7 flex flex-wrap gap-4 text-sm font-semibold">
                        <Link href="/methodology" className="text-accent underline decoration-accent/35 underline-offset-4">Read our methodology</Link>
                        <Link href="/editorial-policy" className="text-accent underline decoration-accent/35 underline-offset-4">Editorial policy</Link>
                        <Link href="/privacy" className="text-accent underline decoration-accent/35 underline-offset-4">Privacy policy</Link>
                    </div>
                </div>
                <div className="card-media aspect-[4/3]">
                    <Image src="/proof/brief-profile-step.png" alt="YourAura profile inputs used to personalize a reading" fill className="object-cover" sizes="(max-width: 1024px) 100vw, 45vw" />
                </div>
            </div>
        </section>
    );
}

export function EducationalGuidesSection() {
    return (
        <section id="guides" className="bg-background px-5 py-20 md:py-24" aria-labelledby="guides-title">
            <div className="container mx-auto max-w-6xl">
                <div className="flex max-w-3xl gap-4">
                    <BookOpen className="mt-1 h-7 w-7 shrink-0 text-accent" aria-hidden="true" />
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">Learn before you believe</p>
                        <h2 id="guides-title" className="mt-3 font-playfair text-3xl font-bold text-foreground md:text-5xl">Explore the system behind your result</h2>
                    </div>
                </div>
                <div className="mt-9 grid gap-4 md:grid-cols-3">
                    {guides.map((guide) => (
                        <Link key={guide.href} href={guide.href} className="card-quiet card-interactive flex h-full flex-col p-5">
                            <h3 className="font-playfair text-xl font-semibold text-foreground">{guide.label}</h3>
                            <p className="mt-3 text-sm leading-6 text-muted-foreground">{guide.body}</p>
                            <span className="card-footer text-sm font-bold text-accent">Explore <ArrowRight size={15} aria-hidden="true" /></span>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
}
